import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ListingPicker, type PickedListing, type ListingType } from '@/components/dashboard/internal/ListingPicker';
import { Loader2, ShieldCheck, LogIn } from 'lucide-react';
import { toast } from 'sonner';

interface PackageRow {
  id: string;
  name: string;
  audience: string;
  price_etb: number;
  duration_days: number;
  features: any;
  is_popular: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: PackageRow | null;
}

type Tier = 'hot' | 'premium' | 'spotlight';
type Placement = 'home' | 'top_of_search' | 'sidebar';

const TIER_FROM_NAME = (name: string): Tier => {
  const n = name.toLowerCase();
  if (n.includes('super') || n.includes('spotlight')) return 'spotlight';
  if (n.includes('premium')) return 'premium';
  return 'hot';
};

export function CheckoutDialog({ open, onOpenChange, pkg }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<PickedListing | null>(null);
  const [pickedType, setPickedType] = useState<ListingType>('property');
  const [tier, setTier] = useState<Tier>('hot');
  const [placement, setPlacement] = useState<Placement>('home');
  const [paymentRef, setPaymentRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync default tier with package on open
  const initialTier = pkg ? TIER_FROM_NAME(pkg.name) : 'hot';
  if (open && pkg && tier !== initialTier && !submitting) {
    // initialise once per open
    setTier(initialTier);
  }

  const reset = () => {
    setPicked(null); setPaymentRef(''); setSubmitting(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    if (!user || !pkg) return;
    if (!picked) { toast.error('Please pick a listing to boost'); return; }
    setSubmitting(true);
    const storedType = picked.type === 'commercial' ? 'property' : picked.type;
    const { data, error } = await supabase.from('boost_orders' as any).insert({
      user_id: user.id,
      package_id: pkg.id,
      listing_type: storedType,
      listing_id: picked.id,
      tier,
      placement,
      duration_days: pkg.duration_days,
      amount_etb: pkg.price_etb,
      payment_ref: paymentRef || null,
      status: paymentRef ? 'paid' : 'pending',
    } as any).select('id').single();
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    toast.success(
      paymentRef
        ? 'Order submitted — your boost will activate after payment review.'
        : 'Order created — complete payment and submit the reference to activate.'
    );
    handleClose(false);
    navigate('/dashboard');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Checkout — {pkg?.name}
            {pkg?.is_popular && <Badge variant="secondary">Popular</Badge>}
          </DialogTitle>
          <DialogDescription>
            ETB {Number(pkg?.price_etb || 0).toLocaleString()} · {pkg?.duration_days} days
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="space-y-4 py-2">
            <Alert>
              <LogIn className="h-4 w-4" />
              <AlertDescription>
                Please sign in to continue with this package.
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              <Button asChild>
                <Link to={`/login?redirect=/advertise`}>Sign in to continue</Link>
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">1. Pick the listing to boost</Label>
              <p className="text-xs text-muted-foreground mb-2">Only your approved listings can be boosted.</p>
              <ListingPicker value={picked} onChange={setPicked} initialType={pickedType} onTypeChange={setPickedType} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tier</Label>
                <Select value={tier} onValueChange={(v) => setTier(v as Tier)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="spotlight">Spotlight</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Placement</Label>
                <Select value={placement} onValueChange={(v) => setPlacement(v as Placement)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="top_of_search">Top of search</SelectItem>
                    <SelectItem value="sidebar">Sidebar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Payment reference (optional)</Label>
              <Input
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="e.g. CBE-TX-123456 — submit after paying"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Provide your transfer reference now or later. Boosts activate after our team confirms payment.
              </p>
            </div>

            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Submitting creates a boost order. Our Ad Operations team will review payment and activate your boost
                for {pkg?.duration_days} days. You can track status in your dashboard.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={!picked || submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {paymentRef ? 'Submit order' : 'Create order'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
